import { ReactComponent as FolderIcon } from 'assets/icons/open-folder.svg';
import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import PageGroupModel from '@mm/state/model/PageGroupModel';
import PageModel from '@mm/state/model/PageModel';
import modalActions from '@mm/state/modules/modalsState/actions';
import {
  selectPageGroups,
  selectPages,
} from '@mm/state/selectors/pageSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { PrimaryButton } from '../../buttons/PrimaryButton/PrimaryButton';
import PrimarySelect, {
  DropdownOption,
} from '../../components/PrimarySelect/PrimarySelect';
import { SearchInput } from '../../components/SearchInput/SearchInput';
import { ModalBody, ModalFooter, ModalHeader } from '../modal-helpers';
import './DialoguePickerModal.pcss';
import { DialoguePickerModalProps } from '@mm/state/modules/modalsState/props';

const fakeAllFoldersDropdownOption = {
  id: -999,
  name: 'All Folders',
  sortOrder: -1,
  botId: -999,
};

export const DialoguePickerModal = ({
  onDialogueSelected,
}: DialoguePickerModalProps) => {
  const dialogues = useSelector(selectPages);
  const dialogueFolders = useSelector(selectPageGroups);
  const augmentedDialogueFolders = [
    fakeAllFoldersDropdownOption,
    ...dialogueFolders,
  ];

  const [searchPhrase, setSearchPhrase] = useState('');
  const [selectedFolder, setSelectedFolder] = useState<PageGroupModel>(
    fakeAllFoldersDropdownOption,
  );
  const [selectedDialogue, setSelectedDialogue] = useState<
    undefined | PageModel
  >();
  const dispatch: Dispatch<StoreState> = useDispatch();
  const closeDialoguePickerModal = () => dispatch(modalActions.closeModal());

  const filteredDialogues = useMemo(() => {
    let filteredDialogues = [...dialogues];
    if (selectedFolder !== fakeAllFoldersDropdownOption) {
      filteredDialogues = filteredDialogues.filter(
        d => d.pageGroupId === selectedFolder.id,
      );
    }
    if (searchPhrase) {
      filteredDialogues = filteredDialogues.filter(
        d => d.name.toLowerCase().indexOf(searchPhrase.toLowerCase()) > -1,
      );
    }
    if (selectedDialogue) {
      if (!filteredDialogues.some(d => d.id === selectedDialogue.id)) {
        setSelectedDialogue(undefined);
      }
    }
    return filteredDialogues;
  }, [dialogues, searchPhrase, selectedFolder, selectedDialogue]);

  const customOptionRenderer = (option: undefined | DropdownOption) =>
    option ? (
      <div className="dialogue-picker-modal__folder-option">
        <FolderIcon />
        <div className="dialogue-picker-modal__folder-option-name">
          {option.name}
        </div>
      </div>
    ) : null;

  return (
    <div className="dialogue-picker-modal__container">
      <ModalHeader
        className="dialogue-picker-modal__header"
        closeOverride={() => {
          setSelectedDialogue(undefined);
          closeDialoguePickerModal();
        }}
      >
        <div className="dialogue-picker-modal__header--left-side">
          <div>
            <h2>
              <b>Pick a Dialogue</b>
            </h2>
          </div>
          <SearchInput
            placeholder="Search"
            value={searchPhrase}
            onChange={e => setSearchPhrase(e.target.value)}
            className="dialogue-picker-modal__search-input"
          />
        </div>
      </ModalHeader>
      <ModalBody className="dialogue-picker-modal__body">
        <div className="dialogue-picker-modal__body--container">
          <PrimarySelect
            options={augmentedDialogueFolders}
            onChange={(option: PageGroupModel) => setSelectedFolder(option)}
            value={selectedFolder}
            placeholder="Filter by folder (optional)"
            customOptionRenderer={customOptionRenderer}
            customDropdownStyles={{ zIndex: '99999999' }}
          />
          <div className="dialogue-picker-modal__body--dialogues-container">
            {filteredDialogues.length
              ? filteredDialogues.map(d => (
                  <div
                    key={`dialogueId-${d.id}`}
                    className={
                      d.id === selectedDialogue?.id
                        ? 'dialogue-picker-modal__body--dialogue--selected'
                        : 'dialogue-picker-modal__body--dialogue'
                    }
                    onClick={() => {
                      if (selectedDialogue && selectedDialogue?.id === d.id) {
                        return setSelectedDialogue(undefined);
                      }
                      setSelectedDialogue(d);
                    }}
                  >
                    {d.name}
                  </div>
                ))
              : null}
          </div>
        </div>
      </ModalBody>
      <ModalFooter className="dialogue-picker-modal__footer">
        <PrimaryButton
          onClick={e => {
            closeDialoguePickerModal();
            onDialogueSelected(
              e,
              selectedDialogue
                ? { id: selectedDialogue.id, name: selectedDialogue.name }
                : undefined,
            );
          }}
          disabled={selectedDialogue === undefined}
        >
          Launch this dialogue
        </PrimaryButton>
      </ModalFooter>
    </div>
  );
};
